import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';

import { getVariantWithMinimalPrice } from '../../../../utils/priceVariantsUtils';
import { formatPrice } from '../../../../utils/price';
import { getAltText, getImageUrl } from '../../../../utils/imgUtils';
import { useGetLayoutSize } from '../../../../components-shared/LayoutSizeProvider';
import {
  LABELS_LIMIT,
  ITEM_LAYOUT_TYPE,
  ITEM_IMAGE_PLACEMENT,
  ITEM_IMAGE_RATIO,
  ITEM_IMAGE_RESIZE,
} from '../../constants';
import { MenuItem } from '../../../../types/menu';
import settingsParams from '../../settingsParams';
import type { ResolvedItemAvailability } from '../../../../utils/menuAvailability';

import { DISH_IMAGE_SIZE, DISH_CARD_IMAGE_WIDTH } from './constants';

const IMAGE_RATIO_NAME: Record<number, 'square' | 'standard' | 'wide'> = {
  [ITEM_IMAGE_RATIO.SQUARE]: 'square',
  [ITEM_IMAGE_RATIO.STANDARD]: 'standard',
  [ITEM_IMAGE_RATIO.WIDE]: 'wide',
};

// [width, height] proportions used to request a correctly-sized image.
// Card = landscape, Strip = portrait (same stored index, different aspect).
const IMAGE_RATIO_DIMENSIONS_CARD: Record<number, [number, number]> = {
  [ITEM_IMAGE_RATIO.SQUARE]: [1, 1],
  [ITEM_IMAGE_RATIO.STANDARD]: [4, 3],
  [ITEM_IMAGE_RATIO.WIDE]: [16, 9],
};
const IMAGE_RATIO_DIMENSIONS_STRIP: Record<number, [number, number]> = {
  [ITEM_IMAGE_RATIO.SQUARE]: [1, 1],
  [ITEM_IMAGE_RATIO.STANDARD]: [3, 4],
  [ITEM_IMAGE_RATIO.WIDE]: [9, 16],
};

const getItemPrice = (item: MenuItem): string => {
  if (item.priceVariants?.variants?.length) {
    const minVariant = getVariantWithMinimalPrice(item.priceVariants.variants);

    if (minVariant?.priceInfo?.price) {
      return formatPrice(
        Number(minVariant.priceInfo.price),
        item.price.currency,
      );
    }
  }

  return formatPrice(item.price.amount, item.price.currency);
};

const isItemInStock = (item: MenuItem): boolean =>
  item.orderSettings?.inStock !== false;

type UseDishItemParams = {
  item: MenuItem;
  menuId: string;
  sectionId: string;
  availability?: ResolvedItemAvailability;
  onClick: (
    item: MenuItem,
    menuId: string,
    sectionId: string,
    availability?: ResolvedItemAvailability,
  ) => void;
};

export const useHooks = ({
  item,
  menuId,
  sectionId,
  availability,
  onClick,
}: UseDishItemParams) => {
  const { t } = useTranslation();
  const { layoutSize } = useGetLayoutSize();
  const settings = useSettings();

  const inStock = isItemInStock(item);
  const isAvailable = availability?.isAvailable ?? inStock;
  const visibleLabels = (item.labels ?? []).filter((l) => l.icon?.id);
  const shownLabels = visibleLabels.slice(0, LABELS_LIMIT);
  const extraLabelCount = visibleLabels.length - LABELS_LIMIT;

  const shouldShowImage =
    settings.get(settingsParams.showItemImage) && Boolean(item.image?.id);
  const shouldShowDescription =
    settings.get(settingsParams.showItemDescription) &&
    Boolean(item.description);
  const shouldShowLabels =
    settings.get(settingsParams.showItemLabels) && visibleLabels.length > 0;
  const shouldShowFeatured =
    settings.get(settingsParams.showItemFeatured) && Boolean(item.featured);

  const isCardLayout =
    settings.get(settingsParams.itemLayoutType) === ITEM_LAYOUT_TYPE.CARD;
  const isImageLeft =
    !isCardLayout &&
    settings.get(settingsParams.itemImagePlacement) ===
      ITEM_IMAGE_PLACEMENT.LEFT;

  // The viewer just reflects the stored settings: a site that never touched the
  // panel controls keeps rendering the legacy shape via the `itemImageRatio`
  // default (strip 1:1, card 16:9) and the `itemImageResize` default (Crop).
  const imageRatio = settings.get(settingsParams.itemImageRatio);
  const imageResizeFit =
    settings.get(settingsParams.itemImageResize) === ITEM_IMAGE_RESIZE.FIT;
  const imageRatioName = IMAGE_RATIO_NAME[imageRatio] ?? 'square';
  const ratioDimensions = isCardLayout
    ? IMAGE_RATIO_DIMENSIONS_CARD
    : IMAGE_RATIO_DIMENSIONS_STRIP;
  const [ratioW, ratioH] = ratioDimensions[imageRatio] ?? [1, 1];

  const handleClick = () => {
    onClick(item, menuId, sectionId, availability);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const priceText = isAvailable
    ? getItemPrice(item)
    : t('menu_olo.itemUnavailable.itemPrice', { price: getItemPrice(item) });

  // The constrained axis differs per layout: the strip image has a fixed height
  // and derives its width from the ratio, the card image is full width and
  // derives its height. Anchor the request on the constrained axis so narrower
  // ratios request fewer pixels, not more — anchored on width, a 9:16 strip
  // would ask for 300x533 to fill an ~86x152 box.
  const [imageWidth, imageHeight] = isCardLayout
    ? [
        DISH_CARD_IMAGE_WIDTH,
        Math.round((DISH_CARD_IMAGE_WIDTH * ratioH) / ratioW),
      ]
    : [Math.round((DISH_IMAGE_SIZE * ratioW) / ratioH), DISH_IMAGE_SIZE];

  const imageUrl = item.image
    ? getImageUrl(
        item.image,
        imageWidth,
        imageHeight,
        // Fit keeps the source aspect (letterboxed); Crop fills/crops the box.
        imageResizeFit ? 'fit' : 'fill',
      )
    : '';
  const imageAlt = getAltText({ itemName: item.name });

  return {
    layoutSize,
    isAvailable,
    isCardLayout,
    isImageLeft,
    imageRatioName,
    imageResizeFit,
    shownLabels,
    extraLabelCount,
    shouldShowImage,
    shouldShowDescription,
    shouldShowLabels,
    shouldShowFeatured,
    handleClick,
    handleKeyDown,
    priceText,
    imageUrl,
    imageAlt,
  };
};
